import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Button, ButtonGroup, Checkbox, FormControlLabel, Tooltip, Typography } from '@mui/material';
import { isMultiHierarchyAccountSummary } from '@opengov/reporting-types';
import { IHeaderGroupParams, IHeaderParams } from 'ag-grid-community';
import React, { FC, useEffect, useState } from 'react';
import { AG_GRID_GROUP_COLUMN_ID, type MDSContext } from '../agGridConsts';
import { shouldHideColumnGroupHeaders } from '../gridUtils';
import { styles } from './DefaultColumnGroupHeader.styles';

const intl = {
  expandAll: 'Expand All',
  collapseAll: 'Collapse All'
};

const MhTotalsBottomCheckbox: FC<{ initialChecked: boolean; label: string; onChange(next: boolean): void }> = ({
  initialChecked,
  label,
  onChange
}) => {
  const [checked, setChecked] = useState(initialChecked);
  useEffect(() => {
    setChecked(initialChecked);
  }, [initialChecked]);
  const toggle = () => {
    setChecked(!checked);
    onChange(!checked);
  };
  return (
    <FormControlLabel
      sx={styles.mhTotalsFormControlLabel}
      control={<Checkbox size="small" checked={checked} onChange={toggle} />}
      label={
        <Tooltip title={label} placement="top" arrow>
          <Typography variant="bodySmall" component="span" noWrap sx={styles.mhTotalsLabel}>
            {label}
          </Typography>
        </Tooltip>
      }
    />
  );
};

export const DefaultColumnGroupHeader: FC<Partial<IHeaderGroupParams & IHeaderParams>> = (props) => {
  const { api, displayName, column, columnGroup, context } = props;
  const { showTotalsAtBottom } = context.localeText.expandCollapse as { showTotalsAtBottom: string };

  const { onExpandAll, onCollapseAll } = context;
  const ctx = context.get() as MDSContext;
  const { report, columnGroups, inEditMode } = ctx;
  const isMultiHierarchy = report && isMultiHierarchyAccountSummary(report);
  const columnPinned = column ? column.getPinned() : columnGroup?.getPinned();
  const isAutoGroupCol =
    column?.getColId() === AG_GRID_GROUP_COLUMN_ID ||
    columnGroup?.getLeafColumns()?.some((col) => col.getColId() === AG_GRID_GROUP_COLUMN_ID);
  const isLeftmostColumnGroup = isAutoGroupCol && columnPinned === 'left';

  const hideColumnGroupHeaders =
    shouldHideColumnGroupHeaders(columnGroups, inEditMode) || (!inEditMode && !!report?.hideColumnGroupHeaders);
  const isColumnHeader = !!column && !columnGroup;
  // Show buttons in the column header row when group headers are hidden, or in the group header row when they're visible.
  const expandCollapseVisibleInThisRow = isColumnHeader ? hideColumnGroupHeaders : !hideColumnGroupHeaders;
  // When column groups are nested (multiple levels), the auto-group column gets a wrapper at every level.
  // Only the topmost group header row (level 0) should render the controls; deeper levels must stay silent.
  const isTopLevelGroupHeader = !columnGroup || columnGroup.getProvidedColumnGroup().getLevel() === 0;
  const showExpandCollapse =
    isLeftmostColumnGroup && expandCollapseVisibleInThisRow && isTopLevelGroupHeader && !ctx.lockRowExpansion;

  const mhTotalsBottom = showExpandCollapse ? ctx.multiHierarchyShowTotalsAtBottom : undefined;
  const showMhTotalsCheckbox = isMultiHierarchy && mhTotalsBottom;

  const expandCollapseButtons = (
    <>
      <ButtonGroup variant="text" color="secondary">
        <Button onClick={() => onExpandAll({ api })} aria-label={intl.expandAll} startIcon={<KeyboardArrowDownIcon />}>
          {intl.expandAll}
        </Button>
        <Button
          onClick={() => onCollapseAll({ api })}
          aria-label={intl.collapseAll}
          startIcon={<KeyboardArrowUpIcon />}
        >
          {intl.collapseAll}
        </Button>
      </ButtonGroup>
      {showMhTotalsCheckbox ? (
        <MhTotalsBottomCheckbox
          initialChecked={report.showTotalsRows !== false}
          label={showTotalsAtBottom}
          onChange={(next) => {
            mhTotalsBottom.onChange(next);
          }}
        />
      ) : null}
    </>
  );

  return (
    <Box role="presentation" sx={styles.groupHeader}>
      {showExpandCollapse ? (
        expandCollapseButtons
      ) : (
        <Typography variant="bodySmall" sx={styles.groupHeaderText}>
          {displayName}
        </Typography>
      )}
    </Box>
  );
};
